import React from "react"
import { Checkbox, Drawer, Radio, Select, Switch } from "antd"
import SignificanceControl from "shared/ui/Significance/SignificanceControl.component"
import TableStyleControl from "shared/ui/TableStyle/TableStyleControl.component"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { DEFAULT_TABLE_STYLE_CHOICE } from "types/tableStyle.types"
import { MEAN_STAT_NAMES } from "../../compiler/table"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import {
  BASE_RULES,
  BaseRule,
  BoxSize,
  GridSummary,
  MEAN_STAT_TYPES,
  MeanStat,
  SORT_RULES,
  SortRule,
} from "../../types/tabSpecDocument"
import { BASE_CHOICE_TEXT, sortChoiceText } from "../Grid/describe"
import SummaryCheckboxes, { SUMMARY_OPTIONS } from "../shared/SummaryCheckboxes.component"

const CHOICE_STATS = [
  { value: STATISTIC_TYPE.COL_PERCENT, label: "Column %" },
  { value: STATISTIC_TYPE.N, label: "Count" },
  { value: STATISTIC_TYPE.ROW_PERCENT, label: "Row %" },
  { value: STATISTIC_TYPE.INDEX, label: "Index" },
]
const NUMBER_STATS = [
  { value: STATISTIC_TYPE.AVG, label: "Mean" },
  { value: STATISTIC_TYPE.MIN, label: "Minimum" },
  { value: STATISTIC_TYPE.MAX, label: "Maximum" },
  { value: STATISTIC_TYPE.BASE_N, label: "Base" },
]
const MEAN_STAT_OPTIONS = MEAN_STAT_TYPES.map((value) => ({ value, label: MEAN_STAT_NAMES[value] }))
const TOP_BOX = [
  { value: 0, label: "None" },
  { value: 2, label: "Top 2" },
  { value: 3, label: "Top 3" },
]
const BOTTOM_BOX = [
  { value: 0, label: "None" },
  { value: 2, label: "Bottom 2" },
  { value: 3, label: "Bottom 3" },
]
const GRID_SUMMARY: { value: GridSummary; label: string }[] = [
  { value: "top-box", label: "Top box" },
  { value: "mean", label: "Mean" },
  { value: "none", label: "None" },
]

/** Said under the settings that only shape the tables added later. */
const ADDED_LATER = "Applies to tables added from now on."
const SUMMARIES_HINT =
  "Each is a table of its own for every grid added from now on: NPS for grids scored 0 to 10, Sum for number grids, a table per column or loop iteration for a grid with columns or in a loop. With none ticked, new grids get the summary above."
const NO_ROUTING = "Without a questionnaire there is no routing to say who was asked."
const SORT_HINT =
  `Highest first sorts each table's answers by the Totals column, within the answers between nets and statistics, and a grid summary's rows the same way. Exceptions such as "Don't know", and answers kept at the end, stay at the end.`
const HIDE_HINT =
  "An answer nobody gave is left out instead of shown with zeros, and so is a grid summary's row nobody answered. Nets, means and the base always show."
/** The base choices of a project without a questionnaire: nobody is "asked". */
const DATA_ONLY_RULES = BASE_RULES.filter((b) => b !== "asked")

/** The chosen statistics in the order of the options, so the tables list them the same way. */
const inOrder = (
  options: { value: STATISTIC_TYPE }[],
  chosen: STATISTIC_TYPE[]
): STATISTIC_TYPE[] =>
  options.map((o) => o.value).filter((v) => chosen.includes(v))

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <section className="ts-field" aria-label={label}>
    <h3 className="ts-field__label">{label}</h3>
    {children}
  </section>
)

/**
 * The spec's defaults: what every table uses unless it sets its own. Each
 * control writes the defaults at once; tables that follow them change with
 * them. The grid summary changes the summary tables there are, while "None",
 * one table per grid row and hidden questions only shape tables added later.
 * A project without a questionnaire has no "Who was asked" (a stored one
 * runs, and shows, as who answered) and no grid or hidden question settings:
 * they shape questionnaire tables only.
 */
const DefaultsDrawer: React.FC<{
  open: boolean
  onClose: () => void
  getContainer?: () => HTMLElement
}> = ({ open, onClose, getContainer }) => {
  const defaults = useTabSpecStore((s) => s.document.defaults)
  const banners = useTabSpecStore((s) => s.document.banners)
  const routingMessage = useTabSpecStore((s) => s.routingMessage)
  const dataOnly = useTabSpecStore((s) => s.dataOnly)
  const updateDefaults = useTabSpecStore((s) => s.updateDefaults)
  // Shown only: a stored "asked" is not rewritten.
  const shownBase = dataOnly && defaults.base === "asked" ? "answered" : defaults.base

  return (
    <Drawer
      open={open}
      onClose={onClose}
      getContainer={getContainer}
      rootClassName="ts-drawer"
      placement="right"
      width={480}
      title="Defaults for every table"
    >
      <div className="ts-drawer__body">
        <Field label="Banner">
          <Select
            size="small"
            aria-label="Banner"
            className="ts-field__select"
            value={defaults.bannerId}
            options={banners.map((b) => ({ value: b.id, label: b.name }))}
            onChange={(bannerId: string) => updateDefaults({ bannerId })}
          />
        </Field>
        <Field label="Base">
          <Radio.Group
            aria-label="Base"
            value={shownBase}
            onChange={(e) => updateDefaults({ base: e.target.value as BaseRule })}
            options={(dataOnly ? DATA_ONLY_RULES : BASE_RULES).map((b) => ({
              value: b,
              label: BASE_CHOICE_TEXT[b],
            }))}
          />
          {dataOnly ? (
            <p className="ts-hint">{NO_ROUTING}</p>
          ) : (
            routingMessage && <p className="ts-hint">{routingMessage}</p>
          )}
        </Field>
        <Field label="Significance">
          <SignificanceControl
            value={defaults.significance}
            onChange={(significance) => updateDefaults({ significance })}
            helperPlacement="below"
          />
        </Field>
        <Field label="Table style">
          <TableStyleControl
            value={defaults.tableStyle}
            onChange={(tableStyle) =>
              updateDefaults({ tableStyle: tableStyle ?? DEFAULT_TABLE_STYLE_CHOICE })
            }
            drawerTitle="Tab spec table style"
          />
        </Field>
        <Field label="Statistics for choice questions">
          <Checkbox.Group
            options={CHOICE_STATS}
            value={defaults.statistics.categorical}
            onChange={(chosen) =>
              updateDefaults({
                statistics: {
                  ...defaults.statistics,
                  categorical: inOrder(CHOICE_STATS, chosen as STATISTIC_TYPE[]),
                },
              })
            }
          />
        </Field>
        <Field label="Statistics for numbers">
          <Checkbox.Group
            options={NUMBER_STATS}
            value={defaults.statistics.numeric}
            onChange={(chosen) =>
              updateDefaults({
                statistics: {
                  ...defaults.statistics,
                  numeric: inOrder(NUMBER_STATS, chosen as STATISTIC_TYPE[]),
                },
              })
            }
          />
        </Field>
        <Field label="Under every mean, also show">
          <Checkbox.Group
            aria-label="Under every mean, also show"
            options={MEAN_STAT_OPTIONS}
            value={defaults.meanStats ?? []}
            onChange={(chosen) => {
              const meanStats = MEAN_STAT_TYPES.filter((s) => (chosen as MeanStat[]).includes(s))
              // None ticked takes the setting away, so the spec saves as it did before.
              updateDefaults({ meanStats: meanStats.length > 0 ? meanStats : undefined })
            }}
          />
          <p className="ts-hint">
            Number tables that show the mean, and grid summaries of means, show them too while
            they follow the defaults. Grid summaries leave out the mean excluding 0.
          </p>
        </Field>
        <Field label="Answers">
          <div className="ts-field__item">
            <div className="ts-field__row">
              <span>Sort answers</span>
              <Radio.Group
                aria-label="Sort answers"
                name="ts-defaults-sort"
                optionType="button"
                buttonStyle="solid"
                size="small"
                value={defaults.sort ?? "none"}
                options={SORT_RULES.map((rule) => ({ value: rule, label: sortChoiceText(rule, dataOnly) }))}
                onChange={(e) =>
                  // As listed takes the setting away, so the spec saves as it did before.
                  updateDefaults({ sort: (e.target.value as SortRule) === "desc" ? "desc" : undefined })
                }
              />
            </div>
            <p className="ts-hint">{SORT_HINT}</p>
          </div>
          <div className="ts-field__item">
            <div className="ts-field__row">
              <span>Hide answers nobody gave</span>
              <Switch
                size="small"
                aria-label="Hide answers nobody gave"
                checked={defaults.hideEmpty === true}
                onChange={(hideEmpty) => updateDefaults({ hideEmpty: hideEmpty ? true : undefined })}
              />
            </div>
            <p className="ts-hint">{HIDE_HINT}</p>
          </div>
        </Field>
        <Field label="Scales">
          <div className="ts-field__row">
            <span>Top box</span>
            <Radio.Group
              aria-label="Top box"
              optionType="button"
              buttonStyle="solid"
              size="small"
              value={defaults.scale.topBox}
              options={TOP_BOX}
              onChange={(e) =>
                updateDefaults({
                  scale: { ...defaults.scale, topBox: e.target.value as BoxSize },
                })
              }
            />
          </div>
          <div className="ts-field__row">
            <span>Bottom box</span>
            <Radio.Group
              aria-label="Bottom box"
              optionType="button"
              buttonStyle="solid"
              size="small"
              value={defaults.scale.bottomBox}
              options={BOTTOM_BOX}
              onChange={(e) =>
                updateDefaults({
                  scale: { ...defaults.scale, bottomBox: e.target.value as BoxSize },
                })
              }
            />
          </div>
          <div className="ts-field__row">
            <span>Mean</span>
            <Switch
              size="small"
              aria-label="Mean"
              checked={defaults.scale.mean}
              onChange={(mean) => updateDefaults({ scale: { ...defaults.scale, mean } })}
            />
          </div>
        </Field>
        {/* The grid and hidden question settings shape questionnaire tables only. */}
        {!dataOnly && (
          <Field label="Grids">
            <div className="ts-field__item">
              <div className="ts-field__row">
                <span>Summary</span>
                <Radio.Group
                  aria-label="Grid summary"
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                  value={defaults.grid.summary}
                  options={GRID_SUMMARY}
                  onChange={(e) =>
                    updateDefaults({
                      grid: { ...defaults.grid, summary: e.target.value as GridSummary },
                    })
                  }
                />
              </div>
              {/* The summary tables read the setting, so Top box and Mean change them all. */}
              <p className="ts-hint">
                Changes the summary tables that exist. None applies to grids added from now on.
              </p>
            </div>
            <div className="ts-field__item">
              <span>Summaries for new grids</span>
              <SummaryCheckboxes
                label="Summaries for new grids"
                options={SUMMARY_OPTIONS}
                value={defaults.grid.summaries ?? []}
                onChange={(summaries) =>
                  // None ticked takes the setting away: new grids get the summary above, as before.
                  updateDefaults({
                    grid: { ...defaults.grid, summaries: summaries.length > 0 ? summaries : undefined },
                  })
                }
              />
              <p className="ts-hint">{SUMMARIES_HINT}</p>
            </div>
            <div className="ts-field__item">
              <div className="ts-field__row">
                <span>One table per row</span>
                <Switch
                  size="small"
                  aria-label="One table per row"
                  checked={defaults.grid.perRow}
                  onChange={(perRow) => updateDefaults({ grid: { ...defaults.grid, perRow } })}
                />
              </div>
              <p className="ts-hint">{ADDED_LATER}</p>
            </div>
          </Field>
        )}
        {!dataOnly && (
          <Field label="Hidden questions">
            <div className="ts-field__item">
              <div className="ts-field__row">
                <span>Include hidden questions</span>
                <Switch
                  size="small"
                  aria-label="Include hidden questions"
                  checked={defaults.includeHidden}
                  onChange={(includeHidden) => updateDefaults({ includeHidden })}
                />
              </div>
              <p className="ts-hint">{ADDED_LATER}</p>
            </div>
          </Field>
        )}
      </div>
    </Drawer>
  )
}

export default DefaultsDrawer
